import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ButtonComponent } from '@voyagu/portal-design-system';
import { RangePickerCalendarComponent } from '@app/forms/formly/types/range-picker-dropdown/calendar/range-picker-calendar.component';
import { formatStringToDay } from '@app/forms/formly/types/range-picker-dropdown/range-picker-dropdown.helpers';

export interface Day {
  date: number;
  month: number;
  year: number;
  disabled?: boolean;
  selected?: boolean;
  inRange?: boolean;
}

export interface Month {
  days: Day[];
  name: string;
  year: number;
}

@Component({
  selector: 'range-picker',
  templateUrl: './range-picker.component.html',
  styleUrls: ['./range-picker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, RangePickerCalendarComponent],
})
export class RangePickerComponent implements OnInit {
  @Input()
  set selectedValue(value: string[] | null) {
    if (!value) {
      return;
    }

    this.selectedDates = [];

    if (value[0]) {
      const day = formatStringToDay(value[0]);

      if (day) {
        this.selectedDates = [day];
      }
    }

    if (value[1]) {
      const day = formatStringToDay(value[1]);

      if (day) {
        this.selectedDates.push(day);
      }
    }

    this.selectedDates = this.sortDates(this.selectedDates);
    this.updateRange();
  }

  @Output() setDates = new EventEmitter<Day[]>();
  @Output() changeSelect = new EventEmitter<Day[]>();
  @Output() nextMothClick = new EventEmitter<void>();
  @Output() prevMothClick = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  months: Month[] = [];
  selectedDates: Day[] = [];
  currentIndex = 0;

  ngOnInit() {
    this.generateMonthsList();
    this.updateRange();
  }

  protected getMonthData(currentDate: Date): Month {
    return {
      days: this.getMonthDays(currentDate),
      name: currentDate.toLocaleString('default', { month: 'long' }),
      year: currentDate.getFullYear(),
    };
  }

  protected getMonthDays(date: Date): Day[] {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const days = [];
    const startDayOfWeek = start.getDay();
    const today = new Date();

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: 0, year: start.getFullYear(), month: start.getMonth(), disabled: true });
    }

    for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
      days.push({
        date: day.getDate(),
        month: day.getMonth(),
        year: day.getFullYear(),
        disabled:
          (day < today && !this.isToday(day)) ||
          (day.getMonth() >= today.getMonth() &&
            day.getDate() >= today.getDate() &&
            day.getFullYear() > today.getFullYear()),
      });
    }
    return days;
  }

  protected addMonths(date: Date, months: number): Date {
    const newDate = new Date(date);
    newDate.setMonth(date.getMonth() + months);
    if (newDate.getMonth() < 0) {
      newDate.setMonth(11);
      newDate.setFullYear(date.getFullYear() - 1);
    } else if (newDate.getMonth() > 11) {
      newDate.setMonth(0);
      newDate.setFullYear(date.getFullYear() + 1);
    }
    return newDate;
  }

  protected isToday(date: Date) {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  protected prevMonth() {
    if (this.currentIndex) {
      this.currentIndex--;
      this.updateRange();
      this.prevMothClick.emit();
    }
  }

  protected nextMonth() {
    if (!this.isNextMonthCurrent()) {
      this.currentIndex++;
      this.updateRange();
      this.nextMothClick.emit();
    }
  }

  protected isNextMonthCurrent() {
    return this.currentIndex === this.months.length - 2;
  }

  protected selectDate(day: Day): void {
    if (!day.disabled) {
      if (this.selectedDates.length === 2) {
        this.selectedDates = [day];
      } else {
        this.selectedDates.push(day);
      }
      this.selectedDates = this.sortDates(this.selectedDates);
      this.updateRange();
      this.changeSelect.emit(this.selectedDates);
    }
  }

  private updateRange() {
    const [start, end] = this.selectedDates;

    const updateDaySelection = (day: Day): Day => {
      if (!end && start) {
        day.selected = day.date === start?.date && day.month === start?.month;
        day.inRange = false;
      } else {
        day.selected =
          (day.date === start?.date && day.month === start?.month && start?.year === day.year) ||
          (day.date === end?.date && day.month === end?.month && end.year === day.year);
        day.inRange = Boolean(day.date) && this.isDateInRange(start, end, day);
      }

      return day;
    };

    this.months.forEach((month) => (month.days = month.days.map(updateDaySelection)));
  }

  private sortDates(dates: Day[]): Day[] {
    return dates.sort((a, b) => {
      if (a.year === b.year) {
        if (a.month === b.month) {
          return (a.date || 0) - (b.date || 0);
        }
        return a.month - b.month;
      }
      return a.year - b.year;
    });
  }

  private isDateInRange(start: Day, end: Day, day: Day) {
    if (!start || !end || !day) {
      return;
    }

    const startDate = new Date(start.year, start.month, start.date);
    const endDate = new Date(end.year, end.month, end.date);
    const dayDate = new Date(day.year, day.month, day.date);

    return startDate < dayDate && dayDate < endDate;
  }

  private generateMonthsList(): void {
    let currentDate = new Date();
    this.months.push(this.getMonthData(currentDate));

    while (!this.isCurrentMonth(currentDate)) {
      currentDate = this.addMonths(currentDate, 1);
      this.months.push(this.getMonthData(currentDate));
    }
  }

  private isCurrentMonth(currentDate: Date) {
    const today = new Date();
    return (
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear() + 1
    );
  }
}
